import React, { useState, useEffect, useRef } from 'react';
import LeftSidebar from '../Tools/LeftSidebar';
import Topbar from '../Tools/Topbar';
import Pagination from '../Tools/Pagination';
import AddUserModal from './Modals/UserModal/AddUserModal';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import RoleDropdown from '../tabs/Dropdown/RoleDropdown';
import axiosInstance from '../../../api';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const EmsUserManagement = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  const [users, setUsers] = useState([]);
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [filterName, setFilterName] = useState('');
  const [filterRoleId, setFilterRoleId] = useState('');
  const debouncedFilterName = useDebounce(filterName, 500);
  const debouncedFilterRoleId = useDebounce(filterRoleId, 0);

  const initialUserState = {
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    email: '',
    password: '',
    address: '',
    role_id: '',
    role_name: '',
    module_name: 'EMS',
    medical_center_id: 0,
    national_id: '',
    dob: '',
    emp_id: ''
  };
  const [newUser, setNewUser] = useState(initialUserState);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});

  const getUsers = async (filters = {}) => {
    try {
      setIsLoading(true);
      const res = await axiosInstance.get('/ems/users-management', {
        params: {
          role_id: filters.role_id || '',
          name: filters.name || ''
        },
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      setUsers(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      alert('Failed to fetch users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getMedicalCenters = async () => {
    try {
      const res = await axiosInstance.get('/ems/medical-centers', {
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      setMedicalCenters(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to fetch medical centers:', error);
    }
  };

  useEffect(() => {
    getUsers();
    getMedicalCenters();
  }, []);

  useEffect(() => {
    getUsers({ name: debouncedFilterName, role_id: debouncedFilterRoleId });
  }, [debouncedFilterName, debouncedFilterRoleId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
    if (apiErrors[name]) setApiErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleAddUser = async () => {
    try {
      setIsLoading(true);
      setApiErrors({});

      const userData = { ...newUser };

      let response;
      if (isEditMode && selectedUserId) {
        response = await axiosInstance.post(`/ems/editable-user-info/${selectedUserId}`, userData);
        alert('User updated successfully!');
      } else {
        response = await axiosInstance.post('/ems/add-new-user', userData);
        alert('User added successfully!');
      }

      getUsers();
      document.getElementById('closeAddUserModal')?.click();
      resetForm();
    } catch (error) {
      console.error('API Error:', error.response);
      if (error.response?.data?.errors) {
        setApiErrors(error.response.data.errors);
      } else {
        alert(error.response?.data?.message || (isEditMode ? 'Failed to update user!' : 'Failed to add user!'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setIsLoading(true);
        await axiosInstance.delete(`/ems/delete-user/${id}`);
        alert('User deleted successfully!');
        getUsers();
      } catch (error) {
        alert('Failed to delete user!');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setNewUser(initialUserState);
    setIsEditMode(false);
    setSelectedUserId(null);
    setApiErrors({});
  };

  const handleEditUser = (user) => {
    setNewUser({
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      phone: (user.phone || ''),
      email: user.email,
      password: '',
      address: user.address,
      role_id: user.role_id || '',
      role_name: user.role?.name || '',
      module_name: user.module_name || 'EMS',
      medical_center_id: user.role?.name === 'EMS Resource Manager' ? (user.medical_center_id || '0') : user.medical_center_id || 0,
      national_id: user.national_id,
      dob: user.dob,
      emp_id: user.emp_id || ''
    });
    setIsEditMode(true);
    setSelectedUserId(user.id);
    setTimeout(() => {
      const modal = new window.bootstrap.Modal(document.getElementById('bs-user'));
      modal.show();
    }, 100);
  };

  const paginatedUsers = users.slice((page - 1) * limit, page * limit);
  const total = users.length;

  const getMedicalCenterDisplay = (user) => {
    if (user.role?.name === 'EMS Resource Manager' && (!user.medical_center_id || user.medical_center_id === '0' || user.medical_center_id === 0)) {
      return 'All';
    }
    return user.medical_center?.name || 'N/A';
  };

  return (
    <div id="main-wrapper">
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">User Management</h5>
                <p className="card-subtitle mb-0 mt-1">
                  Centralized control for user profiles, roles, and login status.
                </p>
              </div>
              <div className="card-body p-4">
                <div className="filter-search mt-5">
                  <h6 className="px-2 mb-0">Filter</h6>
                  <div className="filter-panel">
                    <form>
                      <div className="form-group col-md-4 col-lg-4 col-12 p-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search By Name"
                          value={filterName}
                          onChange={(e) => setFilterName(e.target.value)}
                        />
                      </div>
                      {/* Filter Dropdown - SHOW Admin */}
                      <div className="form-group col-md-4 col-lg-4 col-12 p-2">
                        <RoleDropdown
                          value={filterRoleId}
                          onChange={(e) => setFilterRoleId(e.target.value)}
                          disabled={isLoading}
                          showAdmin={true}        // Show Admin here
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary m-2"
                        onClick={() => {
                          setFilterName('');
                          setFilterRoleId('');
                        }}
                        disabled={isLoading}
                      >
                        Clear Filter
                      </button>
                    </form>
                    <button
                      className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary"
                      onClick={() => {
                        resetForm();
                        const modal = new window.bootstrap.Modal(document.getElementById('bs-user'));
                        modal.show();
                      }}
                      disabled={isLoading}
                    >
                      <i className="ti ti-circle-plus fs-4 me-2"></i> Add User
                    </button>
                  </div>
                </div>
                <div className="py-3 px-0">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive rounded-2 ros-table">
                      <table className="table text-nowrap customize-table mb-0 align-middle">
                        <thead className="text-dark fs-4">
                          <tr>
                            <th>User ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Medical Center</th>
                            <th>National Id</th>
                            <th>Date Of Birth</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user, index) => (
                            <tr key={user.id}>
                              <td>{(page - 1) * limit + index + 1}</td>
                              <td>{user.first_name} {user.last_name}</td>
                              <td>{user.email}</td>
                              <td>{user.role?.name || 'N/A'}</td>
                              <td>
                                {user.role?.name === 'EMS Team' && user.employee?.medical_center?.name
                                  ? user.employee.medical_center.name
                                  : user.medical_center?.name || 'N/A'}
                              </td>
                              <td>{user.national_id || 'N/A'}</td>
                              <td>{user.dob || 'N/A'}</td>
                              <td>
                                <span className={`round-8 rounded-circle me-2 d-inline-block ${user.is_active === 1 ? 'text-bg-success' : 'text-bg-danger'}`}></span>
                                {user.is_active === 1 ? 'Active' : 'Inactive'}
                              </td>
                              <td>
                                <button
                                  className="h4 me-3 border-0 bg-transparent"
                                  onClick={() => handleEditUser(user)}
                                  disabled={isLoading}
                                >
                                  <i className="ti ti-edit text-warning"></i>
                                </button>
                                <button
                                  className="h4 border-0 bg-transparent"
                                  onClick={() => handleDelete(user.id)}
                                  disabled={isLoading}
                                >
                                  <i className="ti ti-trash text-danger"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <Pagination
                  total={total}
                  limit={limit}
                  page={page}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddUserModal
        newUser={newUser}
        handleInputChange={handleInputChange}
        handleAddUser={handleAddUser}
        isEditMode={isEditMode}
        medicalCenters={medicalCenters}
        isLoading={isLoading}
        errors={apiErrors}
      />
      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );
};

export default EmsUserManagement;